import { FilteredPrivateSeriesCountsForUserQuery } from "#src/api/PrivateSerie.ts";
import { SuperAdminBar } from "#src/components/header/SuperAdminHeader.tsx";
import Container from "#src/components/layout/Container.tsx";
import { useHistory, useLocation } from "#src/components/Router.tsx";
import UserModel from "#src/models/UserModel.ts";
import { useApi } from "#src/stores/Api.tsx";
import { useSession } from "#src/stores/Session.tsx";
import { useStore } from "#src/stores/Store.tsx";
import { useUI } from "#src/stores/UI.tsx";
import {
  dateFormatMed,
  SwimAmericaDomains,
  swimAmericaHideHeader,
} from "#src/utils/constants.ts";
import { useCurrentTimestamp } from "#src/utils/currentDate.ts";
import { moment } from "#src/utils/dateTime.ts";
import { hideHeader } from "#src/utils/hideHeader.ts";
import { modals } from "#src/utils/modals.ts";
import tw from "#src/utils/tw.ts";
import { useMediaQueries } from "#src/utils/useMediaQueries.tsx";
import { IntegrationType, Level } from "#types/code-generator.ts";
import { BorderButton } from "../buttons/BorderButton";
import { FilterUsersModal } from "../modals/FilterUsersModal";
import HeaderButton from "./HeaderButton";
import HeaderCartButton from "./HeaderCartButton";
import HeaderLogo from "./HeaderLogo";
import HeaderNavButton from "./HeaderNavButton";
import ViewingAsHeader from "./ViewingAsHeader";

interface Props {
  className?: string;
  children?: React.ReactNode;
}

export function Header(_props: Props) {
  const currentTimestamp = useCurrentTimestamp();

  const ui = useUI();
  const history = useHistory();
  const store = useStore();
  const session = useSession();
  const location = useLocation();
  const { print } = useMediaQueries();

  const privateSeriesCountsQuery = useApi(
    FilteredPrivateSeriesCountsForUserQuery,
    {
      variables: {
        where: {
          organizationId: store.organization?.id,
          accountOwnerId: store.user?.id,
        },
      },
      skip: !store.user?.isStaff,
    }
  );

  const privateSerieCounts = privateSeriesCountsQuery.privateSeriesCounts;

  function toggleMenu() {
    ui.toggleMenu();
  }

  function searchForUsers() {
    modals.show(FilterUsersModal, {
      levels: [
        Level.Student,
        Level.Admin,
        Level.Instructor,
        Level.AccountOwner,
      ],
      handleOk: (user) => {
        selectUser(user);
      },
    });
  }

  function selectUser(userProfile: UserModel) {
    if (userProfile) {
      if (userProfile.primaryAccount) {
        history.push(`/admin/users/${userProfile.primaryAccount.id}`);
      } else {
        history.push(`/admin/users/${userProfile.id}`);
      }
    }
  }

  let headerLogo = <>{store.organization?.title}</>;
  let headerLink = "/";
  const myLessonsNumber = privateSerieCounts?.count || 0;

  if (store.isApp) {
    headerLogo = (
      <img src="/img/captyn-logo.svg" alt="Captyn" width="91" height="16" />
    );
  }

  if (store.user) {
    if (store.user.isAdminOrAbove) {
      if (store.organization.integrations.includes(IntegrationType.FleetFeet)) {
        headerLogo = (
          <img
            src="/img/fleetfeet-header.jpg"
            alt="FleetFeet"
            width="120"
            height="24"
          />
        );
      } else {
        headerLogo = (
          <img src="/img/captyn-logo.svg" alt="Captyn" width="91" height="16" />
        );
      }

      headerLink = "/admin";

      if (store.user.isSalesOrAbove && store.isApp) {
        headerLink = "/super-admin";
      }
    } else if (store.user.isInstructor) {
      headerLogo = (
        <img src="/img/captyn-logo.svg" alt="Captyn" width="91" height="16" />
      );
      headerLink = "/account";
    } else if (store.user.level === Level.AccountOwner) {
      headerLink = "/account";
    }
  }

  let headerButtons = null;
  const headerNumber = store.unread || 0;

  if (
    store.user &&
    store.user.isStaff &&
    store.isAdminArea &&
    !store.isSuperAdminArea
  ) {
    headerButtons = (
      <div>
        <HeaderButton onClick={searchForUsers}>
          <i aria-hidden className="far fa-magnifying-glass" />
        </HeaderButton>
        <HeaderButton
          to="/admin/pending"
          type="dark"
          isEmpty={headerNumber === 0}
        >
          {store.user.isInstructor ? myLessonsNumber : headerNumber}
        </HeaderButton>
      </div>
    );
  } else if (!store.user) {
    headerButtons = (
      <div>
        <BorderButton
          onClick={() => history.push("/login")}
          type="submit"
          small
        >
          Sign In
        </BorderButton>
      </div>
    );
  } else if (store.isSuperAdminArea) {
    headerButtons = (
      <div>
        <HeaderButton to="/super-admin/organizations/add" className="align-bo">
          <i aria-hidden className="far fa-plus relative top-px" />
        </HeaderButton>
      </div>
    );
  } else if (store.user && !store.isApp) {
    headerButtons = (
      <div>
        <HeaderButton to="/account/settings" type="dark">
          {store.user.firstName && store.user.firstName.slice(0, 1)}
          {store.user.lastName && store.user.lastName.slice(0, 1)}
        </HeaderButton>
      </div>
    );
  }

  if (store.user && store.user.isInstructor) {
    headerButtons = (
      <div>
        <HeaderButton onClick={searchForUsers}>
          <i aria-hidden className="far fa-magnifying-glass" />
        </HeaderButton>
        <HeaderButton to="/admin/schedule">
          <i className="far fa-calendar-check" />
        </HeaderButton>
      </div>
    );
  }

  const totalRegistrations =
    session?.groupRegistrations.length +
    session?.privateSeries.length +
    session?.memberships.length;

  if (totalRegistrations > 0) {
    headerButtons = (
      <div>
        {store.isAdminArea && (
          <span className="mr-2">
            <HeaderButton onClick={searchForUsers}>
              <i aria-hidden className="far fa-magnifying-glass" />
            </HeaderButton>
          </span>
        )}
        <HeaderCartButton />
      </div>
    );
  }

  if (
    hideHeader.some((regex) => regex.test(location.pathname)) ||
    (SwimAmericaDomains.includes(store.organization.subdomain) &&
      swimAmericaHideHeader.some((regex) => regex.test(location.pathname)))
  ) {
    return (
      <>
        {store.user && store.user.isSalesOrAbove && (
          <div className="fixed top-0 left-0 z-40 w-full">
            <SuperAdminBar />
          </div>
        )}
      </>
    );
  }

  const isViewingAs = Boolean(store.user && store.superAdmin);
  const isSuperAdmin = store.user?.isSalesOrAbove;
  const isStaff = store.user?.isStaff;
  const reducedHeader =
    (location.pathname.indexOf("/signup") === 0 &&
      location.pathname !== "/signup/success") ||
    location.pathname === "/terms-of-service" ||
    location.pathname === "/privacy-policy";

  if (reducedHeader) {
    headerLink = "#";

    if (location.pathname === "/signup/success") {
      headerLink = "/admin";
    }
  }

  let departmentLabel = "All Departments";
  const departments = session.departments.filter((d) =>
    store.organization.departments.find((od) => od.id === d.id)
  );

  if (
    (!store.user?.isSalesOrAbove &&
      departments.length < store.user?.departments?.length) ||
    (store.user?.isSalesOrAbove &&
      departments.length < store.organization.departments.length)
  ) {
    departmentLabel = departments.map((d) => d.title).join(", ");
  }

  if (store.user?.departments?.length === 1 && !store.user?.isSalesOrAbove) {
    departmentLabel = store.user?.departments[0].title;
  }

  if (departmentLabel.length > 40) {
    departmentLabel = `${departments.length} Department${
      departments.length !== 1 ? "s" : ""
    }`;
  }

  if (departments.length === 0) {
    departmentLabel = `No Departments`;
  }

  return (
    <div
      className={tw(
        "fixed top-0 left-0 z-50 h-[50px] w-full bg-white shadow print:static",
        isSuperAdmin && !print && "h-[80px] pt-[30px]",
        isSuperAdmin && isViewingAs && "h-[110px]",
        !store.isAdminArea && !isStaff && ""
      )}
    >
      {!store.isAdminArea && !isStaff && (
        <div className="bg-organization absolute top-0 left-0 z-50 h-[2px] w-full"></div>
      )}

      {isSuperAdmin && (
        <div className="fixed top-0 left-0 z-40 w-full">
          <SuperAdminBar />
        </div>
      )}
      <Container full className="flex items-center gap-4">
        <div className="flex min-w-0 flex-1">
          {((reducedHeader && store.user) || !reducedHeader) && (
            <HeaderNavButton onClick={toggleMenu} className="print:hidden">
              {ui.menuOpen ? (
                <i aria-hidden className="far fa-xmark" />
              ) : (
                <i aria-hidden className="far fa-bars" />
              )}
            </HeaderNavButton>
          )}

          <HeaderLogo to={headerLink}>{headerLogo}</HeaderLogo>
        </div>

        {store.organization.hasDepartments &&
          store.user?.isStaff &&
          !reducedHeader && (
            <div className="text-empty truncate text-sm sm:max-w-[130px] print:hidden">
              {departmentLabel}
            </div>
          )}

        {!reducedHeader && <div className="print:hidden">{headerButtons}</div>}

        <div className="hidden print:block">
          {moment(currentTimestamp).format(dateFormatMed)}
        </div>
      </Container>
      {store.superAdmin && <ViewingAsHeader />}
    </div>
  );
}
